// Pure validation helper for the user-editable `displayName` field.
//
// Single source of truth for trim + type + length rules. Imported by both
// the server route (`src/server/routes/auth.ts` PATCH /me) and the vitest
// unit suite. Intentionally framework-free — no Zod, no React — so the
// helper is the orakel for every layer that touches displayName.
//
// Length cap (100 chars) counts UTF-16 code units, matching JS `.length`
// and the `<input maxLength>` attribute. Emoji and CJK are accepted; only
// pathological strings (>100 code units) are rejected.

export interface ValidateOk {
  ok: true;
  value: string;
}
export interface ValidateErr {
  ok: false;
  error: string;
}
export type ValidationResult = ValidateOk | ValidateErr;

export const DISPLAY_NAME_MIN = 1;
export const DISPLAY_NAME_MAX = 100;

export function validateDisplayName(input: unknown): ValidationResult {
  if (typeof input !== 'string') {
    return { ok: false, error: 'displayName muss ein String sein.' };
  }
  const trimmed = input.trim();
  if (trimmed.length < DISPLAY_NAME_MIN) {
    return { ok: false, error: 'displayName darf nicht leer sein.' };
  }
  if (trimmed.length > DISPLAY_NAME_MAX) {
    return {
      ok: false,
      error: `displayName darf max. ${DISPLAY_NAME_MAX} Zeichen lang sein.`,
    };
  }
  // NFC-normalisieren (präventiv, 2026-06-14): gepastete Namen (macOS-NFD)
  // würden sonst die displayName-Prefix-Suche (assets.ts suggest) verfehlen —
  // gleiche Wurzel wie #182/#183. Single Source (auth.ts PATCH /me + admin invite/patch).
  return { ok: true, value: trimmed.normalize('NFC') };
}
